// De entiteiten: één vorm voor overal (owner 13-10, componentenpagina
// HXiZJUE1dgG4jQp2S5kStv editie 2). Dezelfde stukken staan in een paneel, op
// een pagina en in een chatantwoord. Gekozen varianten:
//   bronvermelding A (uitgevers naast elkaar, ná het inzicht) · bronicoon A
//   (siteicoon) · bronnenlijst A (uitklapregels + "en nog N") · bronkaart C
//   (bladeren) · citaat B (streep) · regeling B (regel met stip) · trend A en
//   skill A (regel met label; klik = paneel schuift over) · arbeidsmarkt A
//   (getal en richting) · scores A (streepjes zoals versie 4) · label A
//   (zachte pil) · kerngetal B (strook) · credits B (ring) · spiegel C (blok
//   per profiel).

import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Icoon, Knop, Pil, type Toon } from "./basis";
import { vars } from "./lagen";
import { Menu } from "./menu";
import { Uitklapregel } from "./uitklap";

// ── Bron ───────────────────────────────────────────────────────────────────

export type BronItem = {
  sleutel: string;
  uitgever: string;
  titel?: string;
  datum?: string;
  citaat?: string;
  /** praktijk · meting · onderzoek · beleid … (één woord) */
  soort?: string;
  url?: string;
  onKies?: () => void;
};

/** Het domein van een uitgever voor het siteicoon ("ENISA" heeft er geen). */
const domein = (u: string): string | null => (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(u.trim()) ? u.trim().toLowerCase() : null);

/** Siteicoon van de uitgever, met een lettertegel als het icoon niet laadt. */
export function BronIcoon({ uitgever, klein = false }: { uitgever: string; klein?: boolean }) {
  const d = domein(uitgever);
  const [mis, setMis] = useState(!d);
  const k = klein ? " e-bronic--klein" : "";
  if (mis) return <span className={`e-bronic e-bronic--letter${k}`} aria-hidden>{(uitgever[0] ?? "?").toUpperCase()}</span>;
  return (
    <span className={`e-bronic${k}`} aria-hidden>
      <img src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(d!)}&sz=32`} alt="" loading="lazy" onError={() => setMis(true)} />
    </span>
  );
}

/** Eén bron tegelijk, met bladeren (bronkaart C). */
export function BronKaart({ bronnen, totaal, onAlle }: { bronnen: readonly BronItem[]; totaal?: number; onAlle?: () => void }) {
  const [plek, setPlek] = useState(0);
  const n = bronnen.length;
  const b = bronnen[Math.min(plek, n - 1)];
  if (!b) return null;
  return (
    <div className="e-bronkaart">
      {n > 1 && (
        <div className="e-bronkaart__blader">
          <Knop variant="icoon" icoon={ChevronLeft} aria-label="Vorige bron" onClick={() => setPlek((p) => (p - 1 + n) % n)} />
          <span className="s-meta">
            bron {plek + 1} van {n}
          </span>
          <Knop variant="icoon" icoon={ChevronRight} aria-label="Volgende bron" onClick={() => setPlek((p) => (p + 1) % n)} />
        </div>
      )}
      <div className="e-bronkaart__kop">
        <BronIcoon uitgever={b.uitgever} />
        <strong className="s-afkap">{b.uitgever}</strong>
        {b.datum && <span className="s-meta">{b.datum}</span>}
        {b.soort && <Pil>{b.soort}</Pil>}
      </div>
      {b.titel && <p className="e-bronkaart__titel">{b.titel}</p>}
      {b.citaat && <p className="e-bronkaart__citaat">“{b.citaat}”</p>}
      <div className="e-acties">
        {b.onKies && (
          <Knop variant="zacht" onClick={b.onKies}>
            Bekijk de bron
          </Knop>
        )}
        {b.url && (
          <a className="e-link" href={b.url} target="_blank" rel="noreferrer">
            Origineel <Icoon als={ExternalLink} klein />
          </a>
        )}
        {onAlle && totaal && totaal > n && (
          <Knop variant="link" onClick={onAlle}>
            Alle {totaal} bronnen
          </Knop>
        )}
      </div>
    </div>
  );
}

/** De bronvermelding ná een inzicht: tot drie uitgevers naast elkaar, de rest
 *  als +N. Een klik (of hover-focus) opent de bronkaart met bladeren. */
export function BronVermelding({ bronnen, totaal, onAlle }: { bronnen: readonly BronItem[]; totaal?: number; onAlle?: () => void }) {
  if (!bronnen.length) return null;
  const uitgevers = [...new Set(bronnen.map((b) => b.uitgever))];
  const getoond = uitgevers.slice(0, 3);
  const rest = uitgevers.length - getoond.length;
  return (
    <Menu knopKlasse="e-bronvermelding" label={`Bronnen: ${uitgevers.join(", ")}`} inhoud={<>{getoond.join(" · ")}{rest > 0 && ` +${rest}`}</>}>
      {() => <BronKaart bronnen={bronnen} totaal={totaal} onAlle={onAlle} />}
    </Menu>
  );
}

/** Alle bronnen als uitklapregels; de rest altijd als "en nog N in de kennisbank". */
export function BronnenLijst({ bronnen, totaal }: { bronnen: readonly BronItem[]; totaal?: number }) {
  const [open, setOpen] = useState<string | null>(null);
  const buiten = Math.max(0, (totaal ?? bronnen.length) - bronnen.length);
  return (
    <div className="e-bronnen">
      {bronnen.map((b) => (
        <Uitklapregel
          key={b.sleutel}
          klein
          open={open === b.sleutel}
          onOpen={(o) => setOpen(o ? b.sleutel : null)}
          kop={
            <span className="e-bronregel">
              <BronIcoon uitgever={b.uitgever} />
              <strong className="s-afkap">{b.uitgever}</strong>
              {b.datum && <span className="s-meta">{b.datum}</span>}
            </span>
          }
          rechts={b.soort ? <Pil>{b.soort}</Pil> : undefined}
        >
          {b.titel && <p className="e-bronkaart__titel">{b.titel}</p>}
          {b.citaat && <p className="e-bronkaart__citaat">“{b.citaat}”</p>}
          <div className="e-acties">
            {b.onKies && (
              <Knop variant="link" onClick={b.onKies}>
                Bekijk de bron
              </Knop>
            )}
            {b.url && (
              <a className="e-link" href={b.url} target="_blank" rel="noreferrer">
                Origineel openen <Icoon als={ExternalLink} klein />
              </a>
            )}
          </div>
        </Uitklapregel>
      ))}
      {buiten > 0 && (
        <p className="s-meta e-nog">
          en nog {buiten} {buiten === 1 ? "bron" : "bronnen"} in de kennisbank
        </p>
      )}
    </div>
  );
}

/** Citaat B: een streep links, de bron eronder. */
export function Citaat({ tekst, bron }: { tekst: string; bron?: ReactNode }) {
  return (
    <figure className="e-citaat">
      <blockquote>“{tekst}”</blockquote>
      {bron && <figcaption>{bron}</figcaption>}
    </figure>
  );
}

// ── Regels: trend, skill, regeling ─────────────────────────────────────────

export type Label = { tekst: string; toon?: Toon; uitleg?: string };

function RegelKnop({ onOpen, gekozen, children, className = "" }: { onOpen?: () => void; gekozen?: boolean; children: ReactNode; className?: string }) {
  return (
    <button type="button" className={`e-regel ${className}${gekozen ? " e-regel--gekozen" : ""}`} onClick={onOpen} disabled={!onOpen} aria-current={gekozen || undefined}>
      {children}
    </button>
  );
}

/** Trend A: titel, niveau en bronnen, één label rechts. Klik = paneel schuift over. */
export function TrendRegel({ titel, meta, label, onOpen, gekozen }: { titel: string; meta?: string; label?: Label | null; onOpen?: () => void; gekozen?: boolean }) {
  return (
    <RegelKnop onOpen={onOpen} gekozen={gekozen}>
      <span className="e-regel__tekst">
        <span className="e-regel__titel">{titel}</span>
        {meta && <span className="e-regel__meta">{meta}</span>}
      </span>
      {label && (
        <Pil toon={label.toon} uitleg={label.uitleg}>
          {label.tekst}
        </Pil>
      )}
    </RegelKnop>
  );
}

/** Skill A: titel, categorie en bronnen, één label, prioriteit rechts. */
export function SkillRegel({ titel, meta, label, prioriteit, onOpen, gekozen }: { titel: string; meta?: string; label?: Label | null; prioriteit?: number | null; onOpen?: () => void; gekozen?: boolean }) {
  return (
    <RegelKnop onOpen={onOpen} gekozen={gekozen}>
      <span className="e-regel__tekst">
        <span className="e-regel__titel">{titel}</span>
        {meta && <span className="e-regel__meta">{meta}</span>}
      </span>
      {label && (
        <Pil toon={label.toon} uitleg={label.uitleg}>
          {label.tekst}
        </Pil>
      )}
      {typeof prioriteit === "number" && <span className="e-regel__cijfer">{komma(prioriteit)}</span>}
    </RegelKnop>
  );
}

/** Regeling B: statusstip · naam · verstrekker en venster · status. */
export function RegelingRegel({ naam, sub, status, toon = "groen", onOpen }: { naam: string; sub?: string; status?: string; toon?: Toon; onOpen?: () => void }) {
  return (
    <RegelKnop onOpen={onOpen} className="e-regel--stip">
      <span className={`e-stip e-stip--${toon}`} aria-hidden />
      <span className="e-regel__tekst">
        <span className="e-regel__titel">{naam}</span>
        {sub && <span className="e-regel__meta">{sub}</span>}
      </span>
      {status && <span className="s-meta e-regel__status">{status}</span>}
      {onOpen && <Icoon als={ChevronRight} klein />}
    </RegelKnop>
  );
}

// ── Cijfers ────────────────────────────────────────────────────────────────

export const komma = (n: number, d = 1): string => n.toFixed(d).replace(".", ",");

/** Arbeidsmarkt A: wat het is, het getal, de richting, de bron. */
export function Cijfer({ wat, waarde, richting, bron }: { wat: string; waarde: string; richting?: { tekst: string; op: boolean } | null; bron?: ReactNode }) {
  return (
    <div className="e-cijfer">
      <p className="e-cijfer__wat">{wat}</p>
      <p className="e-cijfer__getal">
        {waarde}
        {richting && <span className={`e-cijfer__richting e-cijfer__richting--${richting.op ? "op" : "neer"}`}>{richting.op ? "↑" : "↓"} {richting.tekst}</span>}
      </p>
      {bron && <p className="e-cijfer__bron">{bron}</p>}
    </div>
  );
}

/** Een meter van vijf streepjes (0–10 in stappen van 2), zoals versie 4. */
export function Streepjes({ w, toon = "accent" }: { w: number; toon?: "accent" | "rust" | "groen" }) {
  const vol = Math.round(Math.max(0, Math.min(10, w)) / 2);
  return (
    <span className="e-streepjes" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`e-streepjes__s${i < vol ? ` e-streepjes__s--${toon}` : ""}`} />
      ))}
    </span>
  );
}

export type MeterRegel = { label: string; waarde: number; woord?: string; omgekeerd?: boolean; toon?: "accent" | "rust" | "groen" };
/** Scores A: label · streepjes · woord · cijfer. Bij `omgekeerd` (moeite) is weinig goed. */
export function Meters({ regels }: { regels: readonly MeterRegel[] }) {
  return (
    <ul className="e-meters">
      {regels.map((r) => (
        <li key={r.label} className="e-meters__regel">
          <span>{r.label}</span>
          <Streepjes w={r.omgekeerd ? 10 - r.waarde : r.waarde} toon={r.toon ?? (r.omgekeerd ? "rust" : "accent")} />
          <span className="e-meters__woord">{r.woord ?? ""}</span>
          <span className="e-meters__cijfer">{komma(r.waarde)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Kerngetal B: getallen naast elkaar in één strook. */
export function Kerngetallen({ items }: { items: readonly { getal: ReactNode; wat: string; onKies?: () => void }[] }) {
  return (
    <div className="e-kerngetallen">
      {items.map((it) =>
        it.onKies ? (
          <button key={it.wat} type="button" className="e-kerngetal" onClick={it.onKies}>
            <strong>{it.getal}</strong>
            <span>{it.wat}</span>
          </button>
        ) : (
          <div key={it.wat} className="e-kerngetal">
            <strong>{it.getal}</strong>
            <span>{it.wat}</span>
          </div>
        ),
      )}
    </div>
  );
}

// ── Credits ────────────────────────────────────────────────────────────────

export type TegoedSoort = { sleutel: string; label: string; gebruikt: number; maximum: number; voorbeeld?: boolean };
export const tegoedOver = (soorten: readonly TegoedSoort[]): number => {
  const max = soorten.reduce((a, s) => a + s.maximum, 0);
  const gebr = soorten.reduce((a, s) => a + Math.min(s.gebruikt, s.maximum), 0);
  return max > 0 ? 1 - gebr / max : 1;
};
const tegoedToon = (deel: number): "groen" | "oranje" | "rood" => (deel <= 0 ? "rood" : deel < 0.2 ? "oranje" : "groen");

/** Credits B: een ring om de initialen; de kleur zegt ruim, bijna op of op. */
export function TegoedRing({ deel, children }: { deel: number; children: ReactNode }) {
  const r = 17;
  const omtrek = 2 * Math.PI * r;
  return (
    <span className="e-ring" data-toon={tegoedToon(deel)}>
      <svg viewBox="0 0 38 38" aria-hidden>
        <circle className="e-ring__rust" cx="19" cy="19" r={r} />
        <circle className="e-ring__vol" cx="19" cy="19" r={r} strokeDasharray={omtrek} strokeDashoffset={omtrek * (1 - Math.max(0, Math.min(1, deel)))} />
      </svg>
      {children}
    </span>
  );
}

/** Het tegoed per soort, voor in het accountmenu. */
export function TegoedLijst({ soorten, vult, onMeer, uitleg }: { soorten: readonly TegoedSoort[]; vult?: string; onMeer?: () => void; uitleg?: ReactNode }) {
  return (
    <div className="e-tegoed">
      {uitleg && <p className="e-tegoed__uitleg">{uitleg}</p>}
      <div className="e-tegoed__kop">
        <span>Tegoed deze maand</span>
        {vult && <span>{vult}</span>}
      </div>
      {soorten.map((s) => {
        const over = Math.max(0, s.maximum - s.gebruikt);
        const deel = s.maximum > 0 ? over / s.maximum : 0;
        return (
          <div key={s.sleutel} className="e-tegoed__soort">
            <span>
              {s.label}
            </span>
            <span className="s-meta">{over} over</span>
            <span className="e-tegoed__balk" data-toon={tegoedToon(deel)}>
              <span style={vars({ "--w": `${Math.round(deel * 100)}%` })} />
            </span>
          </div>
        );
      })}
      {onMeer && (
        <Knop variant="zacht" onClick={onMeer}>
          Meer tegoed aanvragen
        </Knop>
      )}
    </div>
  );
}

/** "1 credit" op een knop die tegoed kost. */
export function Kost({ children }: { children: ReactNode }) {
  return <span className="e-kost">{children}</span>;
}

// ── Spiegel ────────────────────────────────────────────────────────────────

export type SpiegelSkill = { titel: string; status: "gat" | "sterk"; termijn?: string; bronnen?: number };
/** Spiegel C: per profiel een vlak met aandeel, tellingen en de skills. */
export function SpiegelBlok({ profiel, aandeel, skills }: { profiel: string; aandeel: number; skills: readonly SpiegelSkill[] }) {
  const gaten = skills.filter((s) => s.status === "gat").length;
  return (
    <section className="e-spiegel">
      <h4 className="e-spiegel__kop">{profiel}</h4>
      <div className="e-spiegel__aandeel">
        <span className="e-spiegel__balk">
          <span style={vars({ "--w": `${aandeel}%` })} />
        </span>
        <span className="s-meta">{aandeel}% van uw mensen</span>
      </div>
      <div className="e-spiegel__telling">
        {gaten > 0 && <span className="e-spiegel__gat">{gaten} {gaten === 1 ? "gat" : "gaten"}</span>}
        {skills.length - gaten > 0 && <span className="e-spiegel__sterk">{skills.length - gaten} sterk</span>}
      </div>
      <ul className="e-spiegel__skills">
        {skills.map((s) => (
          <li key={s.titel}>
            <span className={`e-stip e-stip--${s.status === "gat" ? "oranje" : "groen"}`} aria-hidden />
            <span>
              {s.titel}
              {(s.termijn || s.bronnen) && (
                <span className="s-meta">
                  {" "}
                  · {[s.termijn, s.bronnen ? `${s.bronnen} bronnen` : null].filter(Boolean).join(" · ")}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Markeert iets zonder echte data. Owner 14-10 (Clipy 7rjedc8b5v1x 0:13):
 *  "voorbeeld, overal, ik wil die tekst niet": de bouwsteen toont niets meer,
 *  maar blijft staan zodat zichtbaar is wáár het voorbeeld zit in de code. */
export function Voorbeeld(_props: { children?: ReactNode }) {
  return null;
}

